import { useRef, useState, useEffect } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { 
  ArrowLeft, 
  ArrowRight,
  Users, 
  CheckCircle, 
  Utensils, 
  Sun, 
  Coffee, 
  Award, 
  Building2, 
  MapPin,
  Phone,
  Sparkles,
  Calendar
} from 'lucide-react'
import { getRoomBySlug, rooms } from '../data/rooms'

export default function RaumDetail() {
  const { slug } = useParams<{ slug: string }>()
  const room = slug ? getRoomBySlug(slug) : null
  const heroRef = useRef<HTMLDivElement>(null)
  
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  // Raum-Galerie-Bilder (rein statisch über room.galleryImages)
  const [roomGalleryImages, setRoomGalleryImages] = useState<string[]>([])
  const [isLoadingGallery, setIsLoadingGallery] = useState(true)
  const [visibleImagesCount, setVisibleImagesCount] = useState(12) // Initial 12 Bilder anzeigen

  useEffect(() => {
    if (!room) return
    setIsLoadingGallery(true)
    setRoomGalleryImages(room.galleryImages || [])
    setIsLoadingGallery(false)
  }, [room])

  if (!room) {
    return <Navigate to="/raeume" replace />
  }

  // Get other rooms for recommendations
  const otherRooms = rooms.filter(r => r.id !== room.id).slice(0, 3)

  // Icon-Mapping für Features
  const getFeatureIcon = (feature: string) => {
    const lowerFeature = feature.toLowerCase()
    if (lowerFeature.includes('grill') || lowerFeature.includes('küche')) {
      return <Utensils className="h-4 w-4 text-brand-400" />
    }
    if (lowerFeature.includes('terrasse') || lowerFeature.includes('außen')) {
      return <Sun className="h-4 w-4 text-brand-400" />
    }
    if (lowerFeature.includes('bar') || lowerFeature.includes('weinstube')) {
      return <Coffee className="h-4 w-4 text-brand-400" />
    }
    return <CheckCircle className="h-4 w-4 text-brand-400" />
  }

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Compact Banner with Room Image
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src={
              room.slug === 'fest-scheune' ? '/assets/heros/festscheunehero.JPG' :
              room.slug === 'landhaus' ? '/assets/heros/landhaushero.jpg' :
              room.slug === 'weinstube' ? '/assets/heros/weinstubehero.JPG' :
              room.slug === 'innenhof' ? '/assets/home_pics/HK_277.JPG' :
              '/assets/heros/hero_bilder.jpg'
            }
            alt={room.name}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        {/* Ruby Glow */}
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        {/* Back Navigation */}
        <div className="absolute top-24 left-6 md:left-12 z-20">
          <Link
            to="/raeume"
            className="group inline-flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 group-hover:bg-white/20 transition-all duration-300">
              <ArrowLeft className="h-4 w-4" />
            </div>
            <span className="text-sm uppercase tracking-[0.15em] font-medium">Alle Räume</span>
          </Link>
        </div>
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <MapPin className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">{room.subtitle}</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            {room.name}
          </motion.h1>

          {/* Capacity & Quick Info */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-3"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/30 backdrop-blur-xl border border-brand-800/40 rounded-full">
              <Users className="w-4 h-4 text-brand-400" />
              <span className="text-white text-sm">{room.capacity}</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Building2 className="w-4 h-4 text-white/60" />
              <span className="text-white/60 text-sm">Historisches Ambiente</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTENT SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-28">
        {/* Background Glows */}
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            
            {/* Main Content - Left */}
            <div className="lg:col-span-3 space-y-12">
              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Beschreibung</p>
                
                <div className="space-y-6">
                  {room.fullDescription.split('\n\n').map((paragraph, index) => (
                    <p 
                      key={index}
                      className={index === 0 
                        ? "text-white text-xl md:text-2xl font-light leading-relaxed" 
                        : "text-white/50 text-base md:text-lg leading-relaxed"
                      }
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
              
              {/* Features List */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Ausstattung</p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {room.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="group flex items-start gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl hover:border-brand-800/30 hover:bg-white/[0.04] transition-all duration-300"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        {getFeatureIcon(feature)}
                      </div>
                      <span className="text-white/70 text-sm pt-2.5">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Quote */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative p-8 bg-gradient-to-br from-brand-800/5 to-transparent border-l-2 border-brand-800/50 rounded-r-xl"
              >
                <Sparkles className="absolute top-6 right-6 w-8 h-8 text-brand-800/20" />
                <p className="font-serif text-lg md:text-xl text-white/70 italic leading-relaxed">
                  „Ein Raum, der Geschichte atmet und Ihr Event unvergesslich macht."
                </p>
              </motion.div>
            </div>
            
            {/* Sidebar - Right */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="sticky top-28">
                {/* Booking Card */}
                <div className="p-8 bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 rounded-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-white">Raum anfragen</h3>
                      <p className="text-white/40 text-sm">Unverbindlich & kostenlos</p>
                    </div>
                  </div>
                  
                  {/* Divider */}
                  <div className="h-px bg-white/10 my-6" />
                  
                  {/* Quick Facts */}
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center justify-between py-3 border-b border-white/5">
                      <span className="text-white/40 text-sm">Kapazität</span>
                      <span className="text-white font-medium">{room.capacity}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-white/5">
                      <span className="text-white/40 text-sm">Ambiente</span>
                      <span className="text-white font-medium">{room.subtitle}</span>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <span className="text-white/40 text-sm">Features</span>
                      <span className="text-white font-medium">{room.features.length} inkludiert</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <Link to="/kontakt">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full px-8 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center justify-center gap-3"
                    >
                      <Phone className="w-4 h-4" />
                      Jetzt anfragen
                    </motion.button>
                  </Link>
                  
                  <p className="text-center text-white/30 text-xs mt-4">
                    Antwort innerhalb von max. 48h
                  </p>
                </div>
                
                {/* Trust Badge */}
                <div className="mt-6 p-6 bg-brand-800/5 border border-brand-800/10 rounded-xl">
                  <div className="flex items-center gap-4">
                    <Award className="w-8 h-8 text-brand-400" />
                    <div>
                      <p className="text-white text-sm font-medium">25+ Jahre Erfahrung</p>
                      <p className="text-white/40 text-xs">Ihr Event in besten Händen</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          GALLERY SECTION
      ════════════════════════════════════════════════════════════ */}
      {!isLoadingGallery && roomGalleryImages.length > 0 && (
        <section className="relative py-20 md:py-28 border-t border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
            {/* Header */}
            <div className="text-center mb-16">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
              >
                Impressionen
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light"
              >
                Galerie
              </motion.h2>
            </div>

            {/* Gallery Grid */}
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
              {roomGalleryImages.slice(0, visibleImagesCount).map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="break-inside-avoid mb-6"
                >
                  <div className="group relative overflow-hidden rounded-xl border border-white/5 hover:border-brand-800/30 transition-all duration-500">
                    <motion.img
                      src={image}
                      alt={`${room.name} - Bild ${index + 1}`}
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* "Mehr anzeigen" Button */}
            {roomGalleryImages.length > visibleImagesCount && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center mt-12"
              >
                <motion.button
                  onClick={() => setVisibleImagesCount(prev => Math.min(prev + 12, roomGalleryImages.length))}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-4 border border-white/20 text-white text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-3 mx-auto"
                >
                  Mehr anzeigen
                  <ArrowRight className="w-4 h-4" />
                  <span className="text-white/40 text-xs normal-case tracking-normal ml-2">
                    ({roomGalleryImages.length - visibleImagesCount} weitere)
                  </span>
                </motion.button>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════════════════════════
          OTHER ROOMS
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="absolute bottom-40 left-20 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Mehr entdecken
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light"
            >
              Weitere <span className="italic text-white/60">Räume</span>
            </motion.h2>
          </div>

          {/* Other Rooms Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {otherRooms.map((otherRoom, index) => (
              <motion.div
                key={otherRoom.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/raeume/${otherRoom.slug}`} className="group block">
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-4">
                    <img
                      src={otherRoom.imageUrl}
                      alt={otherRoom.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute inset-0 bg-brand-800/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Capacity Badge */}
                    <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-xl border border-white/10 rounded-full">
                      <div className="flex items-center gap-2">
                        <Users className="w-3 h-3 text-brand-400" />
                        <span className="text-white text-xs">{otherRoom.capacity}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-brand-400 text-xs uppercase tracking-[0.2em]">{otherRoom.subtitle}</p>
                    <h3 className="font-serif text-xl text-white group-hover:text-brand-400 transition-colors duration-300">
                      {otherRoom.name}
                    </h3>
                    <div className="flex items-center gap-2 text-white/40 text-sm group-hover:text-white/60 transition-colors">
                      <span>Details ansehen</span>
                      <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          
          {/* All Rooms Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/raeume">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 border border-white/20 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-3 mx-auto"
              >
                Alle Räume entdecken
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-800/5 rounded-full blur-[150px]" />
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Award className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Überzeugt?</p>
            
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-white font-light mb-8 leading-tight">
              Buchen Sie<br />
              <span className="italic text-white/60">{room.name}</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Kontaktieren Sie uns für eine persönliche Besichtigung und ein unverbindliches Angebot.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3"
                >
                  <Phone className="w-4 h-4" />
                  Jetzt anfragen
                </motion.button>
              </Link>
              <Link to="/raeume">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 border border-white/30 text-white text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-3"
                >
                  Alle Räume
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </div>
            
            <p className="mt-8 text-white/30 text-xs uppercase tracking-[0.2em]">
              Kostenlos · Unverbindlich · Persönliche Beratung
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
